import type { SolutionPageConfig } from '../types/premium';

export const solutionCroConfig: SolutionPageConfig = {
  storyLead: {
    eyebrow: 'Chapter I — The CRO reality',
    title: 'Trials move at the speed of paperwork. Until now.',
    subtitle:
      'CROs carry the weight of every sponsor promise: faster activation, cleaner compliance, happier sites. Lummo turns that pressure into a system — not a pile of inboxes.',
    quote: '“We stopped chasing documents and started running studies.”',
  },
  storyChapters: [
    {
      id: 'cro-chaos',
      eyebrow: 'Act I',
      title: 'The hidden cost of fragmentation',
      body: 'Protocols in one tool, regulatory packs in another, site lists in spreadsheets. Each handoff adds weeks — and risk — before the first patient is screened.',
      image:
        'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Documents and regulatory paperwork on a desk',
      align: 'left',
    },
    {
      id: 'cro-system',
      eyebrow: 'Act II',
      title: 'One operating layer for the whole trial',
      body: 'Lummo connects regulatory automation, explainable matching, and real-time performance so your team orchestrates LATAM from a single command center.',
      image:
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Analytics dashboard and data visualization on a laptop',
      align: 'right',
      quote: 'Speed without sacrificing the audit trail.',
    },
    {
      id: 'cro-scale',
      eyebrow: 'Act III',
      title: 'Scale without adding headcount',
      body: 'AI agents draft, verify, and monitor — your experts approve and steer. More studies, same rigor, measurable time back on the clock.',
      image:
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Team reviewing performance metrics and charts',
      align: 'left',
    },
  ],
  journeyTitle: 'Imagine running trials at the speed of software',
  metrics: [
    { value: '6', suffix: '×', label: 'Faster site activation' },
    { value: '40', suffix: '%', label: 'Less regulatory rework' },
    { value: '12', suffix: '+', label: 'LATAM regulators mapped' },
    { value: '24', suffix: '/7', label: 'Compliance monitoring' },
  ],
  bentoAudience: 'cro',
  bentoEyebrow: 'CRO superpowers',
  bentoTitle: 'Your operating system for clinical excellence',
  bentoSubtitle: 'Modular AI agents, regulatory automation, and real-time visibility — built for scale.',
  features: [
    {
      id: 'reg',
      title: 'Regulatory autopilot',
      description:
        'Country-ready packages for INVIMA, ANMAT, FDA, EMA — generated from your protocol in minutes.',
      tag: 'Compliance',
      outcome: 'Submissions that move — without the Friday-night scramble.',
      span: 'wide',
    },
    {
      id: 'match',
      title: 'Explainable site matching',
      description:
        'Ranked recommendations with traceable criteria: experience, capacity, therapeutic fit.',
      tag: 'Discovery',
      outcome: 'Defensible picks your sponsors can explain in one slide.',
    },
    {
      id: 'feas',
      title: 'Feasibility simulation',
      description: 'Forecast activation timelines and recruitment before budget is committed.',
      tag: 'Forecast',
      outcome: 'Bid and staff with numbers, not hope.',
    },
    {
      id: 'agents',
      title: 'AI agent fleet',
      description: 'Protocol, regulatory, feasibility, matching, and performance agents working in parallel.',
      tag: 'Orchestration',
      outcome: 'Experts stay in charge; agents handle the repetition.',
      span: 'tall',
    },
    {
      id: 'pay',
      title: 'Milestone payments',
      description: 'Evidence-linked payouts when milestones verify — no spreadsheet reconciliation.',
      tag: 'Finance',
      outcome: 'Finance and ops finally read from the same ledger.',
    },
    {
      id: 'audit',
      title: 'Audit-ready by design',
      description: 'Every action logged, versioned, and exportable for inspections.',
      tag: 'Trust',
      outcome: 'Inspection week becomes a folder click, not a fire drill.',
      span: 'wide',
    },
  ],
  steps: [
    {
      title: 'You create a trial in Lummo.',
      paragraphs: [
        'From the first moment you define a study, the platform becomes your regulatory co-pilot. Upload your protocol — or even a draft — and AI extracts objectives, endpoints, and eligibility in seconds.',
      ],
      highlight: {
        paragraphs: [
          'Lummo automatically designs regulatory documents per country: synopsis, ICFs, IBs — aligned with INVIMA, FDA, EMA, or ANMAT.',
        ],
      },
    },
    {
      title: 'Lummo recommends the best Sites for your protocol.',
      paragraphs: [
        'AI searches verified hospitals and research centers considering therapeutic experience, patient match, regulatory readiness, infrastructure, and performance metrics.',
      ],
      highlight: {
        paragraphs: ['You get a ranked, explainable recommendation — not a static list.'],
        conclusion: 'Act fast with confidence.',
      },
    },
    {
      title: 'You see site compliance in real-time.',
      paragraphs: ['Every Site profile includes validated, updated documentation:'],
      bullets: ['Investigator credentials', 'Ethics committee approvals', 'BPC, GCP, ICFs', 'Contracts and activation readiness'],
      highlight: {
        paragraphs: ['Track missing docs, expiration alerts, and generate country-ready packages in one click.'],
        conclusion: 'No more spreadsheets. No more follow-up emails.',
      },
    },
    {
      title: 'You control trial feasibility with real data.',
      paragraphs: ['Plan recruitment in Latin America with visibility into:'],
      bullets: ['Activation time per region', 'Regulatory timelines per country', 'Site response times and recruitment history'],
      highlight: {
        paragraphs: ['Forecast and simulate feasibility before spending budget.'],
        conclusion: 'Avoid delays. Choose smart.',
      },
    },
    {
      title: 'You launch and monitor — all from Lummo.',
      paragraphs: ['Once sites are selected and activated:'],
      bullets: ['Patient pre-screening and onboarding', 'Follow-ups to reduce dropout', 'AE tracking, milestones, and reporting'],
      highlight: {
        paragraphs: ['Everything logged, auditable, and secure. You stay in control.'],
      },
    },
    {
      title: 'Your team is augmented by AI agents.',
      paragraphs: ['Modular agents assist across the trial lifecycle:'],
      bullets: [
        'Protocol Design Agent — drafts and localizes protocols',
        'Regulatory Builder — submission-ready packages',
        'Trial Feasibility AI — recruitment simulations',
        'Site Matching AI — transparent recommendations',
        'Performance Monitor AI — early risk flags',
      ],
      highlight: {
        paragraphs: ['Speed without sacrificing compliance.'],
      },
    },
  ],
  cta: {
    title: 'Lummo makes your CRO more scalable, efficient, and trusted',
    paragraphs: ['Grow with better infrastructure — not just more headcount.'],
    tagline: 'Your clinical trial operating system — modular, intelligent, compliant.',
    primaryHref: '/contact#lummo-demo-form',
    primaryLabel: 'Book a demo',
    secondaryHref: '/team',
    secondaryLabel: 'Meet the team',
  },
};

export const solutionSitesConfig: SolutionPageConfig = {
  storyLead: {
    eyebrow: 'Chapter I — The site story',
    title: 'Your science is world-class. Your admin shouldn’t slow it down.',
    subtitle:
      'Research sites in LATAM do heroic work — then lose trials to outdated profiles, missing documents, and invisible capacity.',
    quote: '“We went from invisible to invited — without another spreadsheet.”',
  },
  storyChapters: [
    {
      id: 'sites-burden',
      eyebrow: 'Act I',
      title: 'Buried in paperwork, unseen by sponsors',
      body: 'Coordinators re-send the same CVs, chase expirations, and still miss invitations because nobody knows the site is ready today.',
      image:
        'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Modern hospital corridor',
      align: 'left',
    },
    {
      id: 'sites-ready',
      eyebrow: 'Act II',
      title: 'Always audit-ready, always discoverable',
      body: 'A living profile validated by AI — licenses, ethics, contracts — so CROs see readiness, not guesswork.',
      image:
        'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Clinical team in discussion at a healthcare facility',
      align: 'right',
      quote: 'Compliance on autopilot. Reputation on display.',
    },
    {
      id: 'sites-patients',
      eyebrow: 'Act III',
      title: 'Patients arrive prepared, not lost',
      body: 'Digital consent, matched participants, and automated follow-ups keep enrollment moving while your team focuses on care.',
      image:
        'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Doctor consulting with a patient',
      align: 'left',
    },
  ],
  journeyTitle: 'Imagine your site always ready for the next trial',
  metrics: [
    { value: '3', suffix: '×', label: 'More trial invitations' },
    { value: '50', suffix: '%', label: 'Less admin overhead' },
    { value: '100', suffix: '%', label: 'Profile always audit-ready' },
    { value: '0', suffix: '', label: 'Spreadsheets required' },
  ],
  bentoAudience: 'sites',
  bentoEyebrow: 'For research sites',
  bentoTitle: 'Visibility, compliance, and patients — in one place',
  bentoSubtitle: 'Stop chasing paperwork. Start leading studies.',
  features: [
    {
      id: 'profile',
      title: 'Living site profile',
      description: 'Licenses, GCP/BPC, CVs, contracts — reviewed by AI for gaps and expirations.',
      tag: 'Credibility',
      outcome: 'Walk into every audit knowing your file is already complete.',
      span: 'wide',
    },
    {
      id: 'discover',
      title: 'Get discovered',
      description: 'CROs and sponsors find you by therapeutic area, capacity, and performance — not spreadsheets.',
      tag: 'Visibility',
      outcome: 'Trials find you when you are actually ready to run them.',
    },
    {
      id: 'invite',
      title: 'Smart invitations',
      description: 'Matched to studies that fit your strengths and real capacity.',
      tag: 'Fit',
      outcome: 'Say yes to studies that fit — and no to the noise.',
      span: 'tall',
    },
    {
      id: 'recruit',
      title: 'Digital recruitment',
      description: 'Pre-screened patients, eConsent, reminders, and engagement flows.',
      tag: 'Enrollment',
      outcome: 'Enrollment that respects both protocol and people.',
    },
    {
      id: 'pay',
      title: 'Faster payments',
      description: 'Milestone-based payouts when evidence is verified on-platform.',
      tag: 'Cash flow',
      outcome: 'Predictable payouts tied to evidence, not email threads.',
    },
    {
      id: 'support',
      title: 'Human + AI support',
      description: 'Guided onboarding and study ops so your team focuses on care.',
      tag: 'Team',
      outcome: 'Coordinators equipped — not underwater in admin.',
      span: 'wide',
    },
  ],
  steps: [
    {
      title: 'You join Lummo.',
      paragraphs: [
        'From onboarding, Lummo guides document uploads — licenses, certifications, CVs, contracts, ICFs — reviewed for accuracy and regulatory alignment.',
      ],
      highlight: {
        paragraphs: ['AI checks expiration dates and country-specific requirements (INVIMA, ANMAT, FDA, EMA).'],
        conclusion: 'Your profile stays current — effortlessly.',
      },
    },
    {
      title: 'You become visible to CROs and Sponsors.',
      paragraphs: [
        'No more being hidden in spreadsheets. Your site is discoverable by infrastructure, performance, and readiness.',
      ],
      highlight: {
        paragraphs: ['Lummo shows why you are the right site — not just your name.'],
      },
    },
    {
      title: 'You receive matched trial invitations.',
      paragraphs: ['Matching evaluates availability, therapeutic expertise, recruiting power, and patient population.'],
      highlight: {
        paragraphs: ['Rankings powered by real-time compliance and performance data.'],
        conclusion: 'Get picked for studies that fit — skip the rest.',
      },
    },
    {
      title: 'You recruit participants from Lummo.',
      paragraphs: ['When a study starts:'],
      bullets: [
        'Eligible patients from Patient Matching AI',
        'Digital consent flows',
        'Real-time status tracking',
        'Automated follow-ups and reminders',
      ],
      highlight: {
        conclusion: 'From first contact to final visit — every patient on track.',
      },
    },
    {
      title: 'You get paid on time.',
      paragraphs: ['Milestones trigger verified payouts — transparent, traceable, without email chains.'],
      highlight: {
        paragraphs: ['Finance teams love the audit trail. Sites love the predictability.'],
      },
    },
  ],
  cta: {
    title: 'Your site deserves infrastructure as rigorous as your science',
    paragraphs: ['Join the network of LATAM sites building the future of trials.'],
    tagline: 'Compliance on autopilot. Patients at the center.',
    primaryHref: '/contact#lummo-demo-form',
    primaryLabel: 'Book a demo',
    secondaryHref: '/team',
    secondaryLabel: 'Meet the team',
  },
};

export const solutionPatientsConfig: SolutionPageConfig = {
  storyLead: {
    eyebrow: 'Chapter I — The patient journey',
    title: 'Hope should not depend on luck — or a late-night Google search.',
    subtitle:
      'Families facing complex conditions deserve a clear path to relevant trials, in their language, with dignity and privacy.',
    quote: '“The right study found me — I didn’t have to hunt for it.”',
  },
  storyChapters: [
    {
      id: 'patients-search',
      eyebrow: 'Act I',
      title: 'The search that never ends',
      body: 'Misinformation, opaque eligibility, and portals that weren’t built for real people — patients give up before science gets a chance.',
      image:
        'https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Person holding a phone, thinking about health choices',
      align: 'left',
    },
    {
      id: 'patients-match',
      eyebrow: 'Act II',
      title: 'Matched with care, not spam',
      body: 'Lummo surfaces trials that truly fit — reviewed by qualified sites — with plain-language consent and HIPAA-grade protection.',
      image:
        'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Healthcare professional supporting a patient',
      align: 'right',
      quote: 'Relevant invitations only. Always.',
    },
    {
      id: 'patients-supported',
      eyebrow: 'Act III',
      title: 'Supported through every visit',
      body: 'Reminders, questionnaires, and a connected study team reduce dropout — because your experience matters as much as the data.',
      image:
        'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=900&q=80&auto=format&fit=crop',
      imageAlt: 'Doctor and patient during a calm consultation',
      align: 'left',
    },
  ],
  journeyTitle: 'Imagine finding the right trial — without the search',
  metrics: [
    { value: '1', suffix: '', label: 'Profile, many matches' },
    { value: '100', suffix: '%', label: 'HIPAA-grade privacy' },
    { value: '0', suffix: '', label: 'Spam invitations' },
    { value: '24', suffix: '/7', label: 'Study team access' },
  ],
  bentoAudience: 'patients',
  bentoEyebrow: 'For patients & families',
  bentoTitle: 'Hope should not depend on luck',
  bentoSubtitle: 'Relevant trials, clear consent, real support — in your language.',
  features: [
    {
      id: 'match',
      title: 'Right trial, right time',
      description: 'AI matches your condition, location, and profile — invitations only when it truly fits.',
      tag: 'Relevance',
      outcome: 'Science can reach you — you should not have to hunt it at 2 a.m.',
      span: 'wide',
    },
    {
      id: 'privacy',
      title: 'Private by design',
      description: 'Encrypted data, clear consent, HIPAA-compliant flows.',
      tag: 'Dignity',
      outcome: 'Your data stays yours; sharing stays explicit.',
    },
    {
      id: 'onboard',
      title: 'Simple onboarding',
      description: 'Digital consent, plain-language explanations, upload from your phone.',
      tag: 'Clarity',
      outcome: 'Consent you can understand without a law degree.',
      span: 'tall',
    },
    {
      id: 'support',
      title: 'Stay supported',
      description: 'Reminders, visits, questionnaires — your team stays connected.',
      tag: 'Continuity',
      outcome: 'You are not alone between visits.',
    },
    {
      id: 'impact',
      title: 'See your impact',
      description: 'Understand how your participation advances medicine for others like you.',
      tag: 'Meaning',
      outcome: 'Participation with a visible line to real progress.',
    },
    {
      id: 'lang',
      title: 'In your language',
      description: 'Materials and support adapted — not translated as an afterthought.',
      tag: 'Access',
      outcome: 'Respect starts with words you fully understand.',
      span: 'wide',
    },
  ],
  steps: [
    {
      title: 'You are matched to the right trial.',
      paragraphs: [
        'Lummo connects you to studies that match your condition, location, and profile — reviewed by qualified sites and regulators.',
      ],
      highlight: {
        paragraphs: ['No search. No guesswork. No spam.'],
        conclusion: 'Just relevance.',
      },
    },
    {
      title: 'You onboard easily — phone or clinic.',
      paragraphs: ['Guided steps include:'],
      bullets: ['Digital consent in your language', 'Clear study explanations', 'Secure information upload', 'Site support when you need it'],
      highlight: {
        paragraphs: ['Everything encrypted, private, and compliant.'],
      },
    },
    {
      title: 'You receive support throughout the study.',
      paragraphs: ['Your team can track visits, schedule follow-ups, send reminders, and reduce dropout risk.'],
      highlight: {
        conclusion: 'Your experience matters — not just the data.',
      },
    },
    {
      title: 'You contribute to science — and know it.',
      paragraphs: [
        'Clear updates on how your participation helps advance medicine — accessible reports and transparency.',
      ],
      highlight: {
        paragraphs: ['You are not a number in a spreadsheet. You are part of progress.'],
      },
    },
  ],
  cta: {
    title: 'The right trial should find you',
    paragraphs: ['Lummo puts patients at the center of clinical innovation in Latin America.'],
    tagline: 'Relevant. Human. Secure.',
    primaryHref: '/contact#lummo-demo-form',
    primaryLabel: 'Book a demo',
    secondaryHref: '/team',
    secondaryLabel: 'Our story',
  },
};

export const homeMetrics = [
  { value: '70', suffix: '%', label: 'Trials delayed in LATAM' },
  { value: '12', suffix: '+', label: 'Countries supported' },
  { value: '6', suffix: '×', label: 'Faster activation potential' },
  { value: '1', suffix: '', label: 'Platform for the full journey' },
];

export const teamMetrics = [
  { value: '5', suffix: '+', label: 'Co-founders with clinical depth' },
  { value: '10', suffix: '+', label: 'Years combined in trials' },
  { value: '2', suffix: '', label: 'Successful exits on the team' },
  { value: '1', suffix: '', label: 'Mission: trials for everyone' },
];
